import React, { useEffect, useMemo, useState } from "react";
import { Outlet, NavLink, useNavigate, useLocation } from "react-router-dom";
import { Home, Search, PlusSquare, Receipt, User, ShoppingBag, MessageCircle, Sparkles, Share2, Coins, Truck } from "lucide-react";
import useDriverStatus from "../hooks/useDriverStatus";
// NOTE: <CartSheet /> and <ItemCustomizeSheet /> live globally at the App.js
// root so public routes like /shop/:shopId and /vendor/:vendorId can open
// them too. See the top-level <GlobalSheets> mount.
import NotificationBell from "./NotificationBell";
import ShareSheet from "./ShareSheet";
import TierUpCelebration from "./TierUpCelebration";
import useTierUpWatcher from "../hooks/useTierUpWatcher";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";
import api from "../lib/api";

const baseNavItems = [
  { to: "/feed", label: "Feed", icon: Home, testid: "nav-feed" },
  { to: "/search", label: "Search", icon: Search, testid: "nav-search" },
  { to: "/orders", label: "Orders", icon: Receipt, testid: "nav-orders" },
  { to: "/profile", label: "Profile", icon: User, testid: "nav-profile" },
];

const IMMERSIVE_PATHS = ["/golive", "/live/"];

export default function AppShell() {
  const { cart, setOpen } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const immersive = IMMERSIVE_PATHS.some((p) => location.pathname.startsWith(p));
  // On the mobile Feed we simplify the top bar to just [logo · messages · cart]
  // so it doesn't overlap the feed tabs + stories rows underneath.
  const isFeed = location.pathname === "/feed" || location.pathname.startsWith("/feed");
  const [unread, setUnread] = useState(0);
  const [credits, setCredits] = useState(0);
  const [shareOpen, setShareOpen] = useState(false);
  const driver = useDriverStatus();

  // Approved vendors see Sell. Customers (and suspended vendors) see Community instead.
  const showSell = !!(
    user &&
    user.role === "vendor" &&
    user.is_vendor &&
    user.is_approved !== false &&
    !user.is_suspended
  );

  const { tierUp, dismiss: dismissTierUp } = useTierUpWatcher();

  const navItems = useMemo(() => {
    const middle = showSell
      ? { to: "/sell", label: "Sell", icon: PlusSquare, testid: "nav-sell" }
      : { to: "/community", label: "Community", icon: Sparkles, testid: "nav-community" };
    return [baseNavItems[0], baseNavItems[1], middle, baseNavItems[2], baseNavItems[3]];
  }, [showSell]);

  useEffect(() => {
    if (immersive) return;
    const tick = async () => {
      try {
        const { data } = await api.get("/messages/unread");
        setUnread(data.unread || 0);
      } catch { /* ignore */ }
    };
    tick();
    const i = setInterval(tick, 8000);
    return () => clearInterval(i);
  }, [immersive, location.pathname]);

  // Lightweight wallet poll so the top bar credit pill stays in sync after
  // sending / receiving a gift. Cheap enough to run every 12s when signed in.
  useEffect(() => {
    if (immersive || !user) return;
    let alive = true;
    const tick = async () => {
      try {
        const { data } = await api.get("/wallet/me");
        if (alive) setCredits(data.wallet?.credits || 0);
      } catch { /* ignore */ }
    };
    tick();
    const i = setInterval(tick, 12000);
    // Listen for instant wallet-changed events from GiftSheet / Credit Store
    // so the pill updates the moment a gift is sent or credits are bought.
    const onChange = (e) => {
      if (alive && typeof e?.detail?.credits === "number") setCredits(e.detail.credits);
      else tick();
    };
    window.addEventListener("hg:wallet-changed", onChange);
    return () => {
      alive = false;
      clearInterval(i);
      window.removeEventListener("hg:wallet-changed", onChange);
    };
  }, [immersive, user, location.pathname]);

  return (
    <div className="min-h-[100dvh] bg-black text-white">
      {/* Top bar (hidden on immersive pages like live stream) */}
      {!immersive && (
        <header
          className="fixed top-0 left-0 right-0 z-40 px-4 pb-2 flex items-center justify-between glass-strong"
          style={{ paddingTop: "calc(env(safe-area-inset-top, 0px) + 12px)" }}
          data-testid="app-header"
        >
          <button
            onClick={() => navigate("/feed")}
            className="flex items-center gap-2"
            data-testid="logo-btn"
          >
            <span className="text-xl font-black tracking-tighter text-gradient-neon whitespace-nowrap">
              Hidden Gem
            </span>
          </button>
          <div className="flex items-center gap-2">
            {driver.is_driver && (
              <button
                onClick={() => navigate("/driver")}
                className="h-10 w-10 grid place-items-center rounded-full bg-violet-500/15 hover:bg-violet-500/25 border border-violet-400/30 text-violet-200 transition-colors"
                data-testid="topbar-driver-btn"
                aria-label="My deliveries"
                title="My deliveries"
              >
                <Truck className="w-5 h-5" />
              </button>
            )}
            {user && (
              <button
                onClick={() => navigate("/credits")}
                className={`${isFeed ? "hidden md:inline-flex" : "inline-flex"} h-10 px-3 items-center gap-1.5 rounded-full bg-gradient-to-r from-yellow-400/15 to-pink-500/15 hover:from-yellow-400/25 hover:to-pink-500/25 border border-yellow-300/30 text-yellow-200 text-xs font-bold transition-colors`}
                data-testid="topbar-credits-btn"
                aria-label="Credits"
                title="Buy credits"
              >
                <Coins className="w-4 h-4" />
                <span className="tabular-nums">{credits.toLocaleString()}</span>
              </button>
            )}
            <button
              onClick={() => setShareOpen(true)}
              className={`${isFeed || driver.is_driver ? "hidden md:grid" : "grid"} h-10 w-10 place-items-center rounded-full bg-white/5 hover:bg-white/10 border border-white/10 transition-colors`}
              data-testid="topbar-share-btn"
              aria-label="Share Hidden Gem"
              title="Share Hidden Gem"
            >
              <Share2 className="w-5 h-5" />
            </button>
            <div className={isFeed ? "hidden md:block" : ""}>
              <NotificationBell />
            </div>
            <button
              onClick={() => navigate("/messages")}
              className="relative h-10 w-10 grid place-items-center rounded-full bg-white/5 hover:bg-white/10 border border-white/10 transition-colors"
              data-testid="open-messages-btn"
              aria-label="Messages"
            >
              <MessageCircle className="w-5 h-5" />
              {unread > 0 && (
                <span
                  className="absolute -top-1 -right-1 min-w-5 h-5 px-1 rounded-full bg-pink-500 text-[10px] font-bold grid place-items-center glow-pink"
                  data-testid="unread-badge"
                >
                  {unread}
                </span>
              )}
            </button>
            <button
              onClick={() => setOpen(true)}
              className="relative h-10 w-10 grid place-items-center rounded-full bg-white/5 hover:bg-white/10 border border-white/10 transition-colors"
              data-testid="open-cart-btn"
              aria-label="Open cart"
            >
              <ShoppingBag className="w-5 h-5" />
              {cart.count > 0 && (
                <span
                  className="absolute -top-1 -right-1 min-w-5 h-5 px-1 rounded-full bg-pink-500 text-[10px] font-bold grid place-items-center glow-pink"
                  data-testid="cart-count-badge"
                >
                  {cart.count}
                </span>
              )}
            </button>
          </div>
        </header>
      )}

      <main className={immersive ? "" : "pt-14 pb-24"} style={immersive ? undefined : { paddingTop: "calc(env(safe-area-inset-top, 0px) + 56px)" }}>
        <Outlet />
      </main>

      {/* Bottom nav (hidden on immersive pages) */}
      {!immersive && (
        <nav
          className="fixed bottom-0 left-0 right-0 z-40 glass-strong px-2 py-2"
          data-testid="bottom-nav"
        >
          <ul className="flex items-stretch justify-around max-w-xl mx-auto">
            {navItems.map(({ to, label, icon: Icon, testid }) => (
              <li key={to}>
                <NavLink
                  to={to}
                  data-testid={testid}
                  className={({ isActive }) =>
                    `flex flex-col items-center gap-1 px-3 py-1.5 rounded-xl transition-all ${
                      isActive
                        ? "text-pink-400"
                        : "text-zinc-400 hover:text-white"
                    }`
                  }
                >
                  {({ isActive }) => (
                    <>
                      <Icon className={`w-6 h-6 ${isActive ? "drop-shadow-[0_0_8px_rgba(255,46,147,0.8)]" : ""}`} strokeWidth={2.2} />
                      <span className="text-[10px] font-medium uppercase tracking-wider">{label}</span>
                    </>
                  )}
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>
      )}

      <ShareSheet open={shareOpen} onOpenChange={setShareOpen} />
      <TierUpCelebration
        open={!!tierUp}
        tierUp={tierUp}
        userName={user?.name}
        onClose={dismissTierUp}
      />
    </div>
  );
}
